import type { TFunction } from 'i18next';

/**
 * Resolves the labels that name the customer's production-planning system (IPS 40, 2.6.1).
 *
 * The dashboard must say "Weight Mercatus plan", not "Weight PMS plan", because the
 * column is an assertion about where the number came from and "PMS" tells an operator
 * nothing they can act on. The name is a per-site setting rather than something derived
 * from `provider_name`, which identifies the integration: a site whose integration is a
 * broker in front of Mercatus would otherwise print the broker's name on every planning
 * system column.
 *
 * When it is not configured the generic name stands. That is the safe default in both
 * directions: a neutral label is merely unhelpful, whereas guessing produces a heading
 * that confidently attributes data to a system the customer may not run.
 *
 * Every label interpolates the one resolved name. A separate set of generic keys is how
 * a label once existed in one branch only ("RGI feed"), so there is deliberately none.
 */
export interface SystemLabels {
    /** Bare system name for a legend or a compact label. */
    name: string;
    /** `bFCR <system>`: the IPS spells it bFCR everywhere. */
    fcr: string;
    /** `RGI <system>`. */
    rgi: string;
    /** Title on the SFR cell naming its denominator (IPS 35). */
    sfrBiomass: string;
    /**
     * Title on a cell whose planning-system signal is not mapped for the pen. The only
     * label without the name: the column header above the cell already carries it.
     */
    cellNotConfigured: string;
}

export function systemLabels(t: TFunction, pmsSystemName: string | null): SystemLabels {
    // A configured name is customer data, so it is interpolated rather than looked up:
    // there is no translation for "Mercatus", only for the sentence around it.
    const system = pmsSystemName?.trim() || t('feedingModule.pms');
    return {
        name: system,
        fcr: t('feedingModule.table.fcrSystem', { system }),
        rgi: t('feedingModule.table.rgiSystem', { system }),
        sfrBiomass: t('feedingModule.table.sfrBiomassDerived', { system }),
        cellNotConfigured: t('feedingModule.dataSource.cellNotConfigured'),
    };
}
